import React, { useRef, useLayoutEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import MediaPicker from './MediaPicker';

const { FiTrash2, FiImage, FiType, FiVideo, FiHash, FiArrowUp, FiArrowDown, FiBold, FiItalic, FiLink, FiList, FiCode, FiLayout } = FiIcons;

const AutoResizeTextarea = ({ value, onChange, placeholder, className }) => {
  const textareaRef = useRef(null);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={1}
      className={className}
      style={{ boxSizing: 'border-box' }}
    />
  );
};

const RichTextEditor = ({ value, onChange, placeholder, className }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el || isFocused) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value || '';
    }
  }, [value, isFocused]);

  const handleFormat = (command, valueArg = null) => {
    document.execCommand(command, false, valueArg);
    if (editorRef.current) {
      onChange({ target: { value: editorRef.current.innerHTML } });
    }
  };

  const handleLink = () => {
    const url = window.prompt('Enter URL');
    if (url) handleFormat('createLink', url);
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange({ target: { value: editorRef.current.innerHTML } });
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-1 mb-2 pb-2 border-b border-gray-200">
        <button type="button" onClick={() => handleFormat('bold')} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" onMouseEnter={e=>e.currentTarget.style.color='var(--brand-primary)'} onMouseLeave={e=>e.currentTarget.style.color=''} aria-label="Bold">
          <SafeIcon icon={FiBold} className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => handleFormat('italic')} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" onMouseEnter={e=>e.currentTarget.style.color='var(--brand-primary)'} onMouseLeave={e=>e.currentTarget.style.color=''} aria-label="Italic">
          <SafeIcon icon={FiItalic} className="w-4 h-4" />
        </button>
        <button type="button" onClick={handleLink} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" onMouseEnter={e=>e.currentTarget.style.color='var(--brand-primary)'} onMouseLeave={e=>e.currentTarget.style.color=''} aria-label="Link">
          <SafeIcon icon={FiLink} className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          handleInput();
        }}
        onInput={handleInput}
        data-placeholder={placeholder}
        className="rich-editor w-full min-h-[6rem] border border-gray-300 rounded-md p-3 text-sm overflow-auto bg-white"
        style={{ minHeight: '6rem' }}
      />
    </div>
  );
};

const ElementEditor = ({ element, updateElement, removeElement, moveUp, moveDown, isFirst, isLast, onOpenMediaPicker }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 relative group">
      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={moveUp}
          disabled={isFirst}
          className="text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed p-1" onMouseEnter={e=>e.currentTarget.style.color='var(--brand-primary)'} onMouseLeave={e=>e.currentTarget.style.color=''}
          aria-label="Move section up"
        >
          <SafeIcon icon={FiArrowUp} className="w-4 h-4" />
        </button>
        <button
          onClick={moveDown}
          disabled={isLast}
          className="text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed p-1" onMouseEnter={e=>e.currentTarget.style.color='var(--brand-primary)'} onMouseLeave={e=>e.currentTarget.style.color=''}
          aria-label="Move section down"
        >
          <SafeIcon icon={FiArrowDown} className="w-4 h-4" />
        </button>
        <button
          onClick={() => removeElement(element.id)}
          className="text-gray-400 hover:text-red-500 p-1"
          aria-label="Delete section"
        >
          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-2 mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {element.type === 'heading' && <><SafeIcon icon={FiHash}/> Heading</>}
        {element.type === 'paragraph' && <><SafeIcon icon={FiType}/> Paragraph</>}
        {element.type === 'list' && <><SafeIcon icon={FiList}/> List</>}
        {element.type === 'image' && <><SafeIcon icon={FiImage}/> Image</>}
        {element.type === 'youtube' && <><SafeIcon icon={FiVideo}/> YouTube Embed</>}
        {element.type === 'canva' && <><SafeIcon icon={FiLayout}/> Canva</>}
        {element.type === 'iframe' && <><SafeIcon icon={FiCode}/> Embed</>}
      </div>

      {element.type === 'heading' && (
        <div className="flex gap-2">
          <select
            value={element.level}
            onChange={(e) => updateElement(element.id, { level: e.target.value })}
            className="border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="h2">H2</option>
            <option value="h3">H3</option>
          </select>
          <input
            type="text"
            value={element.text || ''}
            onChange={(e) => updateElement(element.id, { text: e.target.value })}
            placeholder="Heading text..."
            className="flex-1 border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      )}

      {element.type === 'paragraph' && (
        <RichTextEditor
          value={element.text || ''}
          onChange={(e) => updateElement(element.id, { text: e.target.value })}
          placeholder="Write your content here..."
          className="w-full"
        />
      )}

      {element.type === 'list' && (
        <div>
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              onClick={() => updateElement(element.id, { ordered: false })}
              className={`text-xs px-2 py-1 rounded border ${!element.ordered ? '' : 'bg-white border-gray-300 text-gray-600'}`}
              style={!element.ordered ? { backgroundColor: 'var(--brand-primary-light)', borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' } : {}}
            >
              Bullets
            </button>
            <button
              type="button"
              onClick={() => updateElement(element.id, { ordered: true })}
              className={`text-xs px-2 py-1 rounded border ${element.ordered ? '' : 'bg-white border-gray-300 text-gray-600'}`}
              style={element.ordered ? { backgroundColor: 'var(--brand-primary-light)', borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' } : {}}
            >
              Numbers
            </button>
          </div>
          <AutoResizeTextarea
            value={element.items ? element.items.join('\n') : ''}
            onChange={(e) => updateElement(element.id, { items: e.target.value.split('\n').filter(i => i.trim() !== '') })}
            placeholder="Enter each item on a new line..."
            className="w-full min-h-[6rem] border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500 overflow-hidden resize-none box-border"
          />
        </div>
      )}

      {(element.type === 'image' || element.type === 'youtube' || element.type === 'canva' || element.type === 'iframe') && (
        <div className={`${element.type === 'image' ? 'flex gap-2' : ''}`}>
          <input
            type="text"
            value={element.url || ''}
            onChange={(e) => updateElement(element.id, { url: e.target.value })}
            placeholder={
              element.type === 'image' ? "/assets/image.png or URL" :
              element.type === 'youtube' ? "YouTube URL" :
              element.type === 'canva' ? "Canva share URL" :
              "iframe URL or embed code"
            }
            className={`flex-1 border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500 ${element.type !== 'image' ? 'w-full' : ''}`}
          />
          {element.type === 'image' && onOpenMediaPicker && (
            <button
              type="button"
              onClick={() => onOpenMediaPicker(element.id)}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Browse
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const RowEditor = ({ row, updateRow, removeRow }) => {
  const [mediaPickerFor, setMediaPickerFor] = useState(null);

  const openMediaPicker = (elementId) => setMediaPickerFor(elementId);
  const closeMediaPicker = () => setMediaPickerFor(null);
  const selectMediaImage = (url) => {
    if (mediaPickerFor) {
      updateElement(mediaPickerFor, { url });
    }
    closeMediaPicker();
  };

  const addElement = (col, type) => {
    const newElement = {
      id: `el-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      col,
      type,
      ...(type === 'heading' ? { level: 'h2', text: '' } : {}),
      ...(type === 'paragraph' ? { text: '' } : {}),
      ...(type === 'list' ? { items: [], ordered: false } : {}),
      ...((type === 'image' || type === 'youtube' || type === 'canva' || type === 'iframe') ? { url: '' } : {})
    };

    updateRow(row.id, {
      elements: [...row.elements, newElement]
    });
  };

  const updateElement = (elementId, updates) => {
    updateRow(row.id, {
      elements: row.elements.map(el => el.id === elementId ? { ...el, ...updates } : el)
    });
  };

  const removeElement = (elementId) => {
    updateRow(row.id, {
      elements: row.elements.filter(el => el.id !== elementId)
    });
  };

  const moveElement = (elementId, direction) => {
    const elements = [...row.elements];
    const index = elements.findIndex(el => el.id === elementId);
    if (index === -1) return;

    const col = elements[index].col;

    if (direction === 'up') {
      for (let i = index - 1; i >= 0; i--) {
        if (elements[i].col === col) {
          [elements[index], elements[i]] = [elements[i], elements[index]];
          break;
        }
      }
    } else if (direction === 'down') {
      for (let i = index + 1; i < elements.length; i++) {
        if (elements[i].col === col) {
          [elements[index], elements[i]] = [elements[i], elements[index]];
          break;
        }
      }
    }

    updateRow(row.id, { elements });
  };

  return (
    <div className="relative mb-8 bg-white p-6 rounded-xl shadow-sm border border-gray-200 group">
      <div className="absolute -left-3 -top-3 text-xs font-bold px-2 py-1 rounded shadow-sm" style={{ backgroundColor: 'var(--brand-primary-light)', color: 'var(--brand-primary)' }}>
        {row.columns} Column Row
      </div>

      <button
        onClick={() => removeRow(row.id)}
        className="absolute -right-3 -top-3 bg-red-100 text-red-600 p-1.5 rounded-full shadow-sm hover:bg-red-200 transition-colors opacity-0 group-hover:opacity-100"
      >
        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
      </button>

      <div className="flex gap-6 mt-4">
        <Column
          colIndex={0}
          row={row}
          addElement={addElement}
          updateElement={updateElement}
          removeElement={removeElement}
          moveElement={moveElement}
          onOpenMediaPicker={openMediaPicker}
        />
        {row.columns === 2 && (
          <Column
            colIndex={1}
            row={row}
            addElement={addElement}
            updateElement={updateElement}
            removeElement={removeElement}
            moveElement={moveElement}
            onOpenMediaPicker={openMediaPicker}
          />
        )}
      </div>

      {mediaPickerFor && (
        <MediaPicker
          onSelect={selectMediaImage}
          onClose={closeMediaPicker}
        />
      )}
    </div>
  );
};

const Column = ({ colIndex, row, addElement, updateElement, removeElement, moveElement, onOpenMediaPicker }) => {
  const colElements = row.elements.filter(el => el.col === colIndex);

  return (
    <div className={`p-4 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 ${row.columns === 2 ? 'w-1/2' : 'w-full'}`}>
      {colElements.map((el, idx) => (
        <ElementEditor
          key={el.id}
          element={el}
          updateElement={updateElement}
          removeElement={removeElement}
          moveUp={() => moveElement(el.id, 'up')}
          moveDown={() => moveElement(el.id, 'down')}
          isFirst={idx === 0}
          isLast={idx === colElements.length - 1}
          onOpenMediaPicker={onOpenMediaPicker}
        />
      ))}

      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => addElement(colIndex, 'heading')} className="text-xs bg-white border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
          <SafeIcon icon={FiHash} /> Heading
        </button>
        <button onClick={() => addElement(colIndex, 'paragraph')} className="text-xs bg-white border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
          <SafeIcon icon={FiType} /> Paragraph
        </button>
        <button onClick={() => addElement(colIndex, 'list')} className="text-xs bg-white border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
          <SafeIcon icon={FiList} /> List
        </button>
        <button onClick={() => addElement(colIndex, 'image')} className="text-xs bg-white border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
          <SafeIcon icon={FiImage} /> Image
        </button>
        <button onClick={() => addElement(colIndex, 'youtube')} className="text-xs bg-white border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
          <SafeIcon icon={FiVideo} /> Video
        </button>
        <button onClick={() => addElement(colIndex, 'canva')} className="text-xs bg-white border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
          <SafeIcon icon={FiLayout} /> Canva
        </button>
        <button onClick={() => addElement(colIndex, 'iframe')} className="text-xs bg-white border border-gray-300 hover:bg-gray-50 px-3 py-1.5 rounded shadow-sm flex items-center gap-1">
          <SafeIcon icon={FiCode} /> Embed
        </button>
      </div>
    </div>
  );
};

export default RowEditor;